import React, { FC } from "react";
import { ScheduleBoxProps } from "../../interface/schedule";

import { Box, InputLabel, Stack, Typography } from "@mui/material";
import useResponsive from "../../hooks/useResponsive";
import { useTheme } from "@mui/material/styles";
import { labelSty } from "../UIElements/styles-input";
import {
  formatDate,
  formatDateWithTime,
  formatTextType,
} from "../../utils/url-id";
import { Dot } from "../UIElements/svgIcons";
import { DeleteOutline } from "@mui/icons-material";
import { useMutation } from "@tanstack/react-query";
import { apiDeleteShift } from "../../api/schedules";
import { useNotificationService } from "../notification";
import { useAuth } from "../../hooks/useAuth";
import { LoadingButton } from "@mui/lab";

const ScheduleInformation: FC<{
  scheduleInfo: any;
  mode: string;
  closeModal: () => void;
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ scheduleInfo, mode, setRefresh, closeModal }) => {
  const isMedium = useResponsive("down", "md", "xl");

  return (
    <>
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: "4",
          padding: 3,
        }}
      >
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 2, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <InputLabel sx={{ ...labelSty, mb: 0 }}>Title</InputLabel>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
              }}
            >
              {formatTextType(scheduleInfo?.schedule_name ?? "")}
            </Typography>
          </Stack>
        </Stack>

        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <InputLabel sx={{ ...labelSty, mb: 0 }}>Description</InputLabel>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
              }}
            >
              {scheduleInfo?.description ?? ""}
            </Typography>
          </Stack>
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <InputLabel sx={{ ...labelSty, mb: 0 }}>Status</InputLabel>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
              }}
            >
              {formatTextType(scheduleInfo?.status ?? "")}
            </Typography>
          </Stack>
        </Stack>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <InputLabel sx={{ ...labelSty, mb: 0 }}>Start Date</InputLabel>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
              }}
            >
              {mode === "Grid"
                ? formatDate(scheduleInfo?.start?.toISOString())
                : formatDate(scheduleInfo?.start_date)}
            </Typography>
          </Stack>
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <InputLabel sx={{ ...labelSty, mb: 0 }}>End Date</InputLabel>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
              }}
            >
              {mode === "Grid"
                ? formatDate(scheduleInfo?.end?.toISOString())
                : formatDate(scheduleInfo?.end_date)}
            </Typography>
          </Stack>
        </Stack>

        <Stack sx={{ width: "100%" }}>
          <InputLabel sx={{ ...labelSty, mb: 0 }}>Shifts</InputLabel>

          {scheduleInfo?.shifts?.map((shift: any, id: number) => (
            <ShiftBox
              key={id}
              shift={shift}
              setRefresh={setRefresh}
              closeModal={closeModal}
            />
          ))}
          {scheduleInfo?.shifts?.length === 0 && (
            <Typography
              variant="body1"
              color={"black"}
              sx={{ fontWeight: 600 }}
            >
              No Shift assigned to this schedule.
            </Typography>
          )}
        </Stack>
      </Box>
    </>
  );
};

export default ScheduleInformation;

const ScheduleBox = ({ shift, time, days }: ScheduleBoxProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        padding: 3,
        background: theme.palette.common.white,
        borderRadius: "7px",
        width: 280,
        m: 2,
      }}
    >
      <Stack
        direction="row"
        alignItems={"center"}
        justifyContent={"space-between"}
        sx={{ m: 2, mb: 3 }}
      >
        <Typography
          sx={{ fontWeight: 700, fontFamily: "Inter" }}
          variant="subtitle2"
          color={"neutral"}
        >
          {shift}
        </Typography>
        <Dot />
      </Stack>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        sx={{ m: 2, mb: 3 }}
      >
        <Stack>
          <Typography
            sx={{ fontSize: "10px", fontFamily: "Inter" }}
            color={"neutral"}
          >
            Time
          </Typography>
          <Typography
            sx={{ fontWeight: 700, fontFamily: "Inter" }}
            variant="subtitle2"
            color={"neutral"}
          >
            {time}
          </Typography>
        </Stack>
        <Stack>
          <Typography
            sx={{ fontSize: "10px", fontFamily: "Inter" }}
            color={"neutral"}
          >
            Days
          </Typography>
          <Typography
            sx={{ fontWeight: 700, fontFamily: "Inter" }}
            variant="subtitle2"
            color={"neutral"}
          >
            {days}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

const ShiftBox = ({ shift, setRefresh, closeModal }: any) => {
  const { showNotification } = useNotificationService();
  const { token } = useAuth();

  const mutation = useMutation({
    mutationFn: apiDeleteShift,
    onSuccess: (data) => {
      // Handle error
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
      }
      //  Handle success
      if ("data" in data) {
        setRefresh();
        closeModal();
      }
    },
    onError: (error) => {
      console.error("Process failed", error);
      // Handle error
      showNotification("Process failed", "error");
    },
  });

  const handleDelete = () => {
    const values = { shift_id: shift?.shift, token: token };
    mutation.mutate(values);
  };

  return (
    <Stack
      direction={"row"}
      alignItems={"center"}
      justifyContent={"space-between"}
      sx={{
        my: 3,
        borderBottom: "1px solid #e0e0e0",
        pb: 2,
      }}
    >
      {/* Left side - User name */}
      <Stack>
        <InputLabel sx={{ ...labelSty, mb: 0 }}>Assigned To</InputLabel>
        <Typography
          color={"black"}
          variant="body2"
          sx={{ fontWeight: 600, fontFamily: "Poppins", whiteSpace: "nowrap" }}
        >
          {shift?.assigned_to}
        </Typography>
      </Stack>

      {/* Right side - Start time, End time, and Delete button */}
      <Stack direction={"row"} alignItems={"center"} spacing={4}>
        <Stack sx={{ minWidth: "180px" }}>
          <InputLabel sx={{ ...labelSty, mb: 0 }}>Start Time</InputLabel>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Poppins",
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            {formatDateWithTime(shift.start_time)}
          </Typography>
        </Stack>

        <Stack sx={{ minWidth: "180px" }}>
          <InputLabel sx={{ ...labelSty, mb: 0 }}>End Time</InputLabel>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Poppins",
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            {formatDateWithTime(shift?.end_time)}
          </Typography>
        </Stack>

        <LoadingButton
          loading={mutation.isPending}
          color="error"
          onClick={handleDelete}
          startIcon={<DeleteOutline />}
          sx={{
            minWidth: "auto",
            height: "fit-content",
            mt: 2,
          }}
        ></LoadingButton>
      </Stack>
    </Stack>
  );
};
